import React, { useRef, useState, useEffect } from "react";
import "./styles/editor.css";

const DEFAULT_PRESETS = ["None", "Cinematic", "Vintage", "Dreamy", "BW"];

const secondsToHHMMSS = (s) => {
  if (!isFinite(s)) return "0:00";
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  const min = Math.floor((s / 60) % 60);
  const hr = Math.floor(s / 3600);
  return (hr > 0 ? hr + ":" : "") + min + ":" + sec;
};

export default function App() {
  const videoRef = useRef(null);
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [preset, setPreset] = useState("None");
  const [aiTasks, setAiTasks] = useState({ removeBg: false });
  const [isPlaying, setIsPlaying] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const u = URL.createObjectURL(f);
    setFile(f);
    setUrl(u);
  };

  useEffect(() => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const onLoaded = () => setDuration(v.duration || 0);
    const onTime = () => setCurrentTime(v.currentTime);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
    };
  }, [url]);

  const filterStyle = () => {
    let f = `brightness(${brightness / 100}) contrast(${contrast / 100}) saturate(${saturation / 100})`;
    if (preset === "BW") f += " grayscale(1)";
    if (preset === "Dreamy") f += " blur(1px)";
    return f;
  };

  const buildExportJob = () => ({
    name: file?.name || "untitled",
    trim: { start: trimStart, end: trimEnd },
    filters: { brightness, contrast, saturation, preset },
    speed,
    volume,
    aiTasks,
  });

  const renderToServer = () => {
    console.log("Prepared job:", buildExportJob());
    alert("Export job prepared (check console)");
  };

  return (
    <div className="editor-container" style={{ background: "#0d0d0d", color: "#fff" }}>
      {/* Top Header */}
      <header className="editor-toolbar" style={{ padding: "10px 16px", borderBottom: "1px solid #222" }}>
        <h1 style={{ fontSize: 18 }}>🎬 Zclipr Editor</h1>
        <input type="file" accept="video/*" onChange={onFile} style={{ marginTop: 6 }} />
      </header>

      {/* Video Preview */}
      <main style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "16px" }}>
        {url ? (
          <video
            ref={videoRef}
            src={url}
            controls
            style={{
              width: "100%",
              maxWidth: 420,
              borderRadius: 12,
              border: "2px solid #333",
              objectFit: "contain",
              filter: filterStyle(),
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        ) : (
          <div style={{ color: "#999", textAlign: "center" }}>
            <p>🎥 Upload a video to start editing</p>
          </div>
        )}
      </main>

      {/* Bottom Control Panel */}
      <footer
        style={{
          borderTop: "1px solid #222",
          padding: "10px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          background: "#111",
        }}
      >
        <div style={{ fontSize: 13, textAlign: "center" }}>
          ⏱ {secondsToHHMMSS(currentTime)} / {secondsToHHMMSS(duration)}
        </div>

        {/* Scrollable Controls */}
        <div
          style={{
            display: "flex",
            overflowX: "auto",
            gap: 8,
            paddingBottom: 6,
            scrollbarWidth: "none",
          }}
        >
          <button className="btn" onClick={() => videoRef.current?.play()}>▶ Play</button>
          <button className="btn" onClick={() => videoRef.current?.pause()}>⏸ Pause</button>
          <button className="btn" onClick={() => setTrimStart(currentTime)}>Trim Start</button>
          <button className="btn" onClick={() => setTrimEnd(currentTime)}>Trim End</button>
          <button className="btn" onClick={() => setSpeed(speed === 1 ? 1.5 : 1)}>Speed {speed}x</button>
          <button className="btn" onClick={() => setVolume(volume === 1 ? 0.5 : 1)}>Vol {Math.round(volume * 100)}%</button>
          <button className="btn" onClick={() => setPreset(preset === "None" ? "Cinematic" : "None")}>Preset: {preset}</button>
          <button className="btn" onClick={() => setAiTasks((a) => ({ ...a, removeBg: !a.removeBg }))}>
            AI BG: {aiTasks.removeBg ? "On" : "Off"}
          </button>
          <button className="btn primary" onClick={renderToServer}>💾 Export</button>
        </div>

        {/* Sliders */}
        <div style={{ display: "grid", gap: 6 }}>
          <label>Brightness {brightness}%</label>
          <input type="range" min="50" max="150" value={brightness} onChange={(e) => setBrightness(+e.target.value)} />
          <label>Contrast {contrast}%</label>
          <input type="range" min="50" max="150" value={contrast} onChange={(e) => setContrast(+e.target.value)} />
          <label>Saturation {saturation}%</label>
          <input type="range" min="50" max="150" value={saturation} onChange={(e) => setSaturation(+e.target.value)} />
        </div>
      </footer>
    </div>
  );
}
